// Deep Link Manager - Handle NFC External Scanning & App Opening
import nfcConfigManager from './nfcConfigManager.js';
import licenseManager from './licenseManager.js';

class DeepLinkManager {
  constructor() {
    this.isInitialized = false;
    this.pendingNfcActivation = null;
  }

  // Initialize deep link handling
  async initialize() {
    if (this.isInitialized) return;

    try {
      console.log('🔗 Initializing Deep Link Manager...');

      // Listen for URL changes (for hash-based routing)
      window.addEventListener('hashchange', this.handleHashChange.bind(this));

      // Check initial URL on load
      await this.checkInitialUrl();

      // Listen for focus events (app returning from background)
      window.addEventListener('focus', this.handleAppFocus.bind(this));

      // Listen for visibility change
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

      this.isInitialized = true;
      console.log('✅ Deep Link Manager initialized');

    } catch (error) {
      console.error('❌ Failed to initialize Deep Link Manager:', error);
    }
  }

  // Check initial URL for NFC parameters
  async checkInitialUrl() {
    try {
      const url = new URL(window.location.href);
      await this.processUrl(url);
    } catch (error) {
      console.error('Failed to check initial URL:', error);
    }
  }

  // Handle hash changes
  async handleHashChange() {
    try {
      const url = new URL(window.location.href);
      await this.processUrl(url);
    } catch (error) {
      console.error('Failed to handle hash change:', error);
    }
  }

  // Handle app focus (returning from background)
  async handleAppFocus() {
    // Check if there's a pending NFC activation
    if (this.pendingNfcActivation) {
      console.log('🔄 App focused with pending NFC activation');
      await this.processNfcActivation(this.pendingNfcActivation);
      this.pendingNfcActivation = null;
    }
  }

  // Handle visibility change
  async handleVisibilityChange() {
    if (!document.hidden && this.pendingNfcActivation) {
      console.log('👁️ App visible with pending NFC activation');
      await this.processNfcActivation(this.pendingNfcActivation);
      this.pendingNfcActivation = null;
    }
  }

  // Process URL for deep link parameters
  async processUrl(url) {
    try {
      const params = new URLSearchParams(url.search);
      const hash = url.hash.substring(1);
      const hashParams = new URLSearchParams(hash);

      // Check for NFC parameter in query string or hash
      const nfcId = params.get('nfc') || hashParams.get('nfc');
      const source = params.get('source') || hashParams.get('source');

      if (nfcId) {
        console.log('🔗 Deep link NFC detected:', { nfcId, source });
        await this.handleNfcDeepLink(nfcId, source);
      }

      // Sprint 3 §3c — inbound knowledge gift (self-contained, offline).
      const giftEnc = params.get('gift') || hashParams.get('gift');
      if (giftEnc) {
        await this.handleGiftDeepLink(giftEnc);
      }

      // Check for other deep link types
      const action = params.get('action') || hashParams.get('action');
      if (action) {
        await this.handleActionDeepLink(action, params, hashParams);
      }

    } catch (error) {
      console.error('Failed to process URL:', error);
    }
  }

  // Decode an inbound gift, drop it in the inbox, and announce it so the UI can
  // offer to open it. Fully client-side — no network involved.
  async handleGiftDeepLink(encoded) {
    try {
      const { decodeGift, addReceivedGift } = await import('./giftManager.js');
      const payload = decodeGift(encoded);
      if (!payload) return;
      const item = addReceivedGift(payload, encoded);
      window.dispatchEvent(new CustomEvent('wayfare-gift-received', {
        detail: { item: item || { type: payload.type, data: payload.data, from: payload.from } },
      }));
      // Tidy the URL so a reload doesn't re-trigger the same gift.
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete('gift');
        window.history.replaceState({}, document.title, u.pathname + u.search + u.hash);
      } catch { /* ignore */ }
    } catch (e) {
      console.warn('[DeepLink] gift decode failed:', e.message);
    }
  }

  // Handle NFC deep link activation
  async handleNfcDeepLink(nfcId, source = null) {
    try {
      console.log('📱 Processing NFC deep link:', { nfcId, source });

      // Validate NFC ID format
      if (!this.isValidNfcId(nfcId)) {
        throw new Error('Invalid NFC ID format');
      }

      // Skip local validation - server will validate
      // Note: NFC key validation moved to server for security
      console.log('🔐 NFC key will be validated by server');

      // Store pending activation if user is not authenticated
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        console.log('⏳ User not authenticated, storing pending NFC activation');
        this.pendingNfcActivation = { nfcId, source };

        // Show authentication prompt
        this.showAuthenticationPrompt(nfcId);
        return;
      }

      // Process immediate activation
      await this.processNfcActivation({ nfcId, source });

    } catch (error) {
      console.error('❌ Failed to handle NFC deep link:', error);
      this.showErrorMessage(`Failed to process NFC activation: ${error.message}`);
    }
  }

  // Process NFC activation
  async processNfcActivation({ nfcId, source }) {
    try {
      console.log('⚡ Processing NFC activation:', { nfcId, source });

      // If user is already premium, show Quick Menu instead of re-activating
      if (licenseManager.isPremiumUser) {
        window.dispatchEvent(new CustomEvent('nfc-quick-menu'));
        this.cleanUrl();
        return;
      }

      // Show loading state
      this.showActivationLoading(nfcId);

      // Attempt activation using license manager
      const result = await licenseManager.grantPremiumAccess(nfcId);

      if (result.success) {
        console.log('✅ NFC activation successful:', result);
        this.showActivationSuccess(result, source);
      } else {
        console.error('❌ NFC activation failed:', result.error);
        this.showActivationError(result.error);
      }

    } catch (error) {
      console.error('❌ NFC activation processing failed:', error);
      this.showActivationError(error.message);
    }
  }

  // Handle action deep links
  async handleActionDeepLink(action, params, hashParams) {
    try {
      console.log('🎯 Processing action deep link:', action);

      switch (action) {
        case 'share':
          await this.handleShareAction(params, hashParams);
          break;

        case 'premium':
          await this.handlePremiumAction(params, hashParams);
          break;

        case 'download':
          await this.handleDownloadAction(params, hashParams);
          break;

        default:
          console.warn('Unknown action:', action);
      }

    } catch (error) {
      console.error('Failed to handle action deep link:', error);
    }
  }

  // Handle share action
  async handleShareAction(params, hashParams) {
    const shareToken = params.get('token') || hashParams.get('token');
    const sharedBy = params.get('by') || hashParams.get('by');

    if (shareToken && sharedBy) {
      console.log('🤝 Processing share link:', { shareToken, sharedBy });

      try {
        const result = await licenseManager.grantSharedPremiumAccess(sharedBy);
        if (result.success) {
          this.showShareSuccess(result, sharedBy);
        } else {
          this.showShareError(result.error);
        }
      } catch (error) {
        this.showShareError(error.message);
      }
    }
  }

  // Handle premium action
  async handlePremiumAction(params, hashParams) {
    console.log('💎 Showing premium features');
    this.navigateToPremium();
  }

  // Handle download action
  async handleDownloadAction(params, hashParams) {
    console.log('📥 Handling download action');
    this.showDownloadPrompt();
  }

  // Validate NFC ID format
  isValidNfcId(nfcId) {
    if (!nfcId || typeof nfcId !== 'string') return false;

    // Basic validation - adjust based on your NFC ID format
    return nfcId.length >= 8 && nfcId.length <= 50;
  }

  // Get current user (interface with auth manager)
  getCurrentUser() {
    try {
      // Import dynamically to avoid circular dependencies
      const authManager = require('./authManager.js').default;
      return authManager.getCurrentUser();
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  // Show authentication prompt
  showAuthenticationPrompt(nfcId) {
    // Dispatch event for UI to handle
    window.dispatchEvent(new CustomEvent('deep-link-auth-required', {
      detail: { nfcId, message: 'Please log in to activate NFC premium access' }
    }));
  }

  // Show activation loading
  showActivationLoading(nfcId) {
    window.dispatchEvent(new CustomEvent('nfc-activation-loading', {
      detail: { nfcId, message: 'Activating premium access...' }
    }));
  }

  // Show activation success
  showActivationSuccess(result, source) {
    window.dispatchEvent(new CustomEvent('nfc-activation-success', {
      detail: {
        type: result.type,
        message: result.message,
        source,
        expiresAt: result.expiresAt
      }
    }));
  }

  // Show activation error
  showActivationError(error) {
    window.dispatchEvent(new CustomEvent('nfc-activation-error', {
      detail: { error, message: `Activation failed: ${error}` }
    }));
  }

  // Show share success
  showShareSuccess(result, sharedBy) {
    window.dispatchEvent(new CustomEvent('share-activation-success', {
      detail: {
        type: result.type,
        message: result.message,
        sharedBy,
        expiresAt: result.expiresAt
      }
    }));
  }

  // Show share error
  showShareError(error) {
    window.dispatchEvent(new CustomEvent('share-activation-error', {
      detail: { error, message: `Share activation failed: ${error}` }
    }));
  }

  // Show generic error message
  showErrorMessage(message) {
    window.dispatchEvent(new CustomEvent('deep-link-error', {
      detail: { message }
    }));
  }

  // Navigate to premium section
  navigateToPremium() {
    window.dispatchEvent(new CustomEvent('navigate-to-premium'));
  }

  // Show download prompt
  showDownloadPrompt() {
    window.dispatchEvent(new CustomEvent('show-download-prompt'));
  }

  // Generate share URL
  async generateShareUrl(nfcId, userEmail) {
    try {
      const config = await nfcConfigManager.getDeepLinkConfig();
      const baseUrl = config.baseUrl;

      const params = new URLSearchParams({
        nfc: nfcId,
        source: 'share',
        by: userEmail,
        t: Date.now().toString() // timestamp for cache busting
      });

      return `${baseUrl}?${params.toString()}`;

    } catch (error) {
      console.error('Failed to generate share URL:', error);
      return null;
    }
  }

  // Generate NFC tag write data
  async generateNfcTagData(nfcId) {
    try {
      const config = await nfcConfigManager.getDeepLinkConfig();
      const url = `${config.baseUrl}?nfc=${encodeURIComponent(nfcId)}&source=nfc_tag`;

      return {
        records: [
          {
            recordType: 'url',
            data: url
          }
        ]
      };

    } catch (error) {
      console.error('Failed to generate NFC tag data:', error);
      return null;
    }
  }

  // Write NFC tag (if browser supports it)
  async writeNfcTag(nfcId) {
    try {
      if (!('NDEFWriter' in window)) {
        throw new Error('NFC writing not supported on this device');
      }

      const tagData = await this.generateNfcTagData(nfcId);
      if (!tagData) {
        throw new Error('Failed to generate tag data');
      }

      const ndef = new NDEFWriter();
      await ndef.write(tagData);

      console.log('✅ NFC tag written successfully');
      return { success: true };

    } catch (error) {
      console.error('❌ Failed to write NFC tag:', error);
      return { success: false, error: error.message };
    }
  }

  // Clean up URL parameters after processing
  cleanUrl() {
    try {
      const url = new URL(window.location.href);

      // Remove NFC and action parameters
      url.searchParams.delete('nfc');
      url.searchParams.delete('source');
      url.searchParams.delete('action');
      url.searchParams.delete('by');
      url.searchParams.delete('token');
      url.searchParams.delete('t');

      // Update URL without page reload
      window.history.replaceState({}, document.title, url.toString());

    } catch (error) {
      console.error('Failed to clean URL:', error);
    }
  }

  // Get pending NFC activation
  getPendingNfcActivation() {
    return this.pendingNfcActivation;
  }

  // Clear pending NFC activation
  clearPendingNfcActivation() {
    this.pendingNfcActivation = null;
  }
}

export default new DeepLinkManager();